import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { Count } from '@/types/count'
import {
  Notification,
  NotificationPreference,
  PushDevice,
  SendNotification,
  SendNotificationResponse,
} from '@/types/notifications'
import PaginatedResponse from '@/types/paginated-response'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useGet, { UseGetOptions } from '../core-hooks/useGet'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useMutation, { UseMutationOptions } from '../core-hooks/useMutation'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import queryKeys from '../query-keys'
import {
  createDetail,
  getDetail,
  getList,
  updateDetail,
} from '../request-wrappers'

const NOTIFICATIONS_URL = '/v2/pd-notify/data/notifications'
const PREFERENCES_URL = '/v2/pd-notify/data/preferences/current-user'
const DEVICES_URL = '/v2/pd-notify/data/devices'
const SEND_URL = '/v2/pd-notify/notifications/send'

const unreadCountQueryKey = [...queryKeys.notifications.all, 'unread-count']
const preferenceQueryKey = ['notification-preference']

type NotificationsCache = InfiniteData<PaginatedResponse<Notification>>
type Rollback = (() => void) | undefined

/** The caller's own recipient row carries read state. pd-notify nests only that one. */
export function isNotificationRead(notification: Notification) {
  return !!notification.recipients[0]?.read_at
}

/**
 * Sets read state on matching rows in every cached page of the notifications
 * list and moves the unread count to match. Returns a function that puts the
 * previous cache back if the request fails.
 */
function useOptimisticReadState() {
  const queryClient = useQueryClient()

  return async function setReadStateInCache(
    shouldChange: (notification: Notification) => boolean,
    read: boolean,
    // Mark all reads rows past the loaded pages too, so the count is set to
    // zero rather than lowered by the loaded rows it changed.
    { clearsCount = false }: { clearsCount?: boolean } = {},
  ) {
    await queryClient.cancelQueries({
      queryKey: queryKeys.notifications.all,
    })
    const previousLists = queryClient.getQueriesData<NotificationsCache>({
      queryKey: queryKeys.notifications.lists(),
    })
    const previousCount = queryClient.getQueryData<Count>(unreadCountQueryKey)
    const readAt = read ? new Date().toISOString() : null

    let changedCount = 0
    queryClient.setQueriesData<NotificationsCache>(
      { queryKey: queryKeys.notifications.lists() },
      (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: page.results.map((notification) => {
              if (!shouldChange(notification)) return notification
              if (isNotificationRead(notification) === read) return notification
              changedCount += 1
              return {
                ...notification,
                recipients: notification.recipients.map((recipient) => ({
                  ...recipient,
                  read_at: readAt,
                })),
              }
            }),
          })),
        }
      },
    )

    if (previousCount) {
      let count: number
      if (clearsCount) {
        count = 0
      } else if (read) {
        count = Math.max(0, previousCount.count - changedCount)
      } else {
        count = previousCount.count + changedCount
      }
      queryClient.setQueryData<Count>(unreadCountQueryKey, {
        ...previousCount,
        count,
      })
    }

    return function rollback() {
      previousLists.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      if (previousCount) {
        queryClient.setQueryData(unreadCountQueryKey, previousCount)
      }
    }
  }
}

/**
 * MARK: Action Hook: Get My Notifications
 * The signed-in user's notifications in the current account, newest first.
 * pd-notify takes the user and account from the token.
 */
export function useGetMyNotifications({
  filters,
  ...options
}: UseGetListOptions<Notification> = {}) {
  const listFilters = { limit: '20', ...filters }
  const query = useGetList<Notification>({
    filters: listFilters,
    queryKey: queryKeys.notifications.list({ filters: listFilters }),
    serviceFn: (serviceFnOptions) =>
      getList<Notification>({
        url: `${NOTIFICATIONS_URL}/current-user`,
        ...serviceFnOptions,
      }),
    ...options,
  })

  return {
    notifications: query.allData,
    ...query,
  }
}

/**
 * MARK: Action Hook: Get Unread Notifications Count
 * Feeds the bell badge. Polls, since nothing pushes new notifications to an open tab.
 */
export function useGetUnreadNotificationsCount(
  options: Omit<UseGetOptions<Count>, 'queryKey'> = {},
) {
  const query = useGet<Count>({
    queryKey: unreadCountQueryKey,
    serviceFn: (serviceFnOptions) =>
      getDetail<Count>({
        url: `${NOTIFICATIONS_URL}/current-user/unread-count`,
        ...serviceFnOptions,
      }),
    // Once a minute, since pd-notify has no live channel to the browser
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    ...options,
  })

  return {
    unreadCount: query.data?.count ?? 0,
    ...query,
  }
}

type SetReadStateVariables = { id: string; read: boolean }

/** onMutate is left out: its result is the rollback these hooks run on error. */
type ReadStateMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, unknown, TVariables, Rollback>,
  'onMutate'
>

/**
 * MARK: Action Hook: Set Notification Read State
 * Marks one notification read or unread for the signed-in user only.
 */
export function useSetNotificationReadState({
  onError,
  ...options
}: ReadStateMutationOptions<Notification, SetReadStateVariables> = {}) {
  const setReadStateInCache = useOptimisticReadState()
  const mutation = useMutation<
    Notification,
    unknown,
    SetReadStateVariables,
    Rollback
  >({
    sideEffectQueryKeys: [queryKeys.notifications.all],
    mutationFn: ({ id, read }) =>
      createDetail<object, Notification>({
        url: `${NOTIFICATIONS_URL}/${id}/${read ? 'read' : 'unread'}`,
        item: {},
      }).then(({ data }) => data),
    onMutate: ({ id, read }) =>
      setReadStateInCache((notification) => notification.id === id, read),
    onError: (error, variables, rollback, context) => {
      rollback?.()
      onError?.(error, variables, rollback, context)
    },
    ...options,
  })

  return {
    setNotificationReadState: mutation.mutate,
    setNotificationReadStateAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Mark All Notifications Read
 */
export function useMarkAllNotificationsRead({
  onError,
  ...options
}: ReadStateMutationOptions<Count, void> = {}) {
  const setReadStateInCache = useOptimisticReadState()
  const mutation = useMutation<Count, unknown, void, Rollback>({
    sideEffectQueryKeys: [queryKeys.notifications.all],
    mutationFn: () =>
      createDetail<object, Count>({
        url: `${NOTIFICATIONS_URL}/current-user/mark-all-read`,
        item: {},
      }).then(({ data }) => data),
    onMutate: () =>
      setReadStateInCache(() => true, true, { clearsCount: true }),
    onError: (error, variables, rollback, context) => {
      rollback?.()
      onError?.(error, variables, rollback, context)
    },
    ...options,
  })

  return {
    markAllNotificationsRead: mutation.mutate,
    markAllNotificationsReadAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get My Notification Preference
 * pd-notify answers `email_enabled: true` when the user has never saved one.
 */
export function useGetMyNotificationPreference(
  options: Omit<UseGetOptions<NotificationPreference>, 'queryKey'> = {},
) {
  const query = useGet<NotificationPreference>({
    queryKey: preferenceQueryKey,
    serviceFn: (serviceFnOptions) =>
      getDetail<NotificationPreference>({
        url: PREFERENCES_URL,
        ...serviceFnOptions,
      }),
    ...options,
  })

  return {
    notificationPreference: query.data,
    ...query,
  }
}

/**
 * MARK: Action Hook: Update My Notification Preference
 */
export function useUpdateMyNotificationPreference(
  options: UseUpdateDetailOptions<NotificationPreference> = {},
) {
  const mutation = useUpdateDetail<NotificationPreference>({
    serviceFn: (serviceFnOptions) =>
      updateDetail({ url: PREFERENCES_URL, ...serviceFnOptions }),
    detailQueryKey: preferenceQueryKey,
    ...options,
  })

  return {
    updateNotificationPreference: mutation.mutate,
    updateNotificationPreferenceAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Register Push Device
 * Upserts this browser's row for the signed-in user in the current account.
 * Safe to call on every load: pd-notify matches on platform and token, so a
 * repeat refreshes the row instead of adding one.
 */
export function useRegisterMyPushDevice(
  options: UseMutationOptions<unknown, unknown, PushDevice> = {},
) {
  const mutation = useMutation<unknown, unknown, PushDevice>({
    // zdjango's bulk PATCH takes a list. The items have no id, since the
    // server finds the row by platform and token.
    mutationFn: (device) =>
      updateDetail<PushDevice[]>({ url: DEVICES_URL, item: [device] }).then(
        ({ data }) => data,
      ),
    ...options,
  })

  return {
    registerMyPushDevice: mutation.mutate,
    registerMyPushDeviceAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Delete My Push Device
 * Removes this browser's rows for the signed-in user in every account, so a
 * signed-out user stops getting pushes here. Call it before signing out,
 * while the session can still authorize it.
 */
export function useDeleteMyPushDevice(
  options: UseMutationOptions<Count, unknown, PushDevice> = {},
) {
  const mutation = useMutation<Count, unknown, PushDevice>({
    mutationFn: (device) =>
      createDetail<PushDevice, Count>({
        url: `${DEVICES_URL}/current-user/delete-device`,
        item: device,
        // Signing out waits on this, so a slow API must not hold it up.
        axiosOptions: { timeout: 5_000 },
      }).then(({ data }) => data),
    ...options,
  })

  return {
    deleteMyPushDevice: mutation.mutate,
    deleteMyPushDeviceAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Send Notification
 * A failed send is retried with the same notification `id`. pd-notify ignores
 * an `id` it has already seen, so a retry never notifies anyone twice.
 */
export function useSendNotification(
  options: UseCreateDetailOptions<
    SendNotification,
    SendNotificationResponse
  > = {},
) {
  const mutation = useCreateDetail<SendNotification, SendNotificationResponse>({
    retry: 2,
    serviceFn: (serviceFnOptions) =>
      createDetail<SendNotification, SendNotificationResponse>({
        url: SEND_URL,
        ...serviceFnOptions,
      }),
    ...options,
  })

  return {
    sendNotification: mutation.mutate,
    sendNotificationAsync: mutation.mutateAsync,
    ...mutation,
  }
}
